import * as Sentry from '@sentry/react';

/**
 * Safe, non-sensitive context attached to captured errors. Never put raw
 * payment credentials (card/bank numbers, CVV, tokens, passwords) in here.
 */
export interface ReportErrorContext {
  featureArea?: string;
  studioId?: string;
  userId?: string;
  parentId?: string;
  studentId?: string;
  classId?: string;
  sessionId?: string;
  registrationId?: string;
  formSource?: string;
  paymentMethodPresent?: boolean;
  errorCode?: string;
  [key: string]: unknown;
}

/**
 * Same Error instance → same event id (StrictMode double-effects, remounts,
 * and route boundary after api/query already captured the same throw).
 * First reporter wins so the on-screen reference code points at the richer
 * earlier event (e.g. `api` / `query`) instead of a second `route` duplicate.
 */
const reportedErrors = new WeakMap<object, string>();

/**
 * Record that this error was already captured (e.g. by Sentry.ErrorBoundary)
 * so a later `reportError` for the same instance reuses that event id.
 */
export function rememberReportedError(
  error: unknown,
  eventId: string,
): void {
  if (error !== null && typeof error === 'object' && eventId) {
    reportedErrors.set(error, eventId);
  }
}

/**
 * Central error reporter. Sends the error to Sentry with a `featureArea` tag
 * (so issues are filterable) and the remaining safe context grouped under
 * `business`. Studio/user identity is attached globally via setSentryUserContext
 * / setSentryStudioContext, so it does not need to be repeated here.
 *
 * Returns the Sentry event id when capture succeeds so crash screens can show
 * a real support reference. Returns `undefined` when Sentry is not initialised
 * (local / development / staging) — never invents a client-only UUID that
 * would look like a support code but does not exist in Sentry.
 */
export function reportError(
  error: unknown,
  context: ReportErrorContext = {},
): string | undefined {
  if (error !== null && typeof error === 'object') {
    const existing = reportedErrors.get(error);
    if (existing !== undefined) {
      return existing;
    }
  }

  const { featureArea, errorCode, ...rest } = context;

  const eventId = Sentry.captureException(error, {
    tags: {
      ...(featureArea ? { featureArea } : {}),
      ...(errorCode ? { errorCode } : {}),
    },
    contexts: {
      business: { featureArea, errorCode, ...rest },
    },
  });

  // Without an active client, captureException is a no-op and returns ''.
  if (!eventId) {
    return undefined;
  }

  if (error !== null && typeof error === 'object') {
    reportedErrors.set(error, eventId);
  }

  return eventId;
}
